import { useEffect, useState } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import Switch from "../../../components/switch/Switch";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import RangeDatePicker from "../../../components/data/date/RangeDatePicker";
import WorkorderStatusSelect from "../../../components/data/workorder-status/WorkorderStatusSelect";
import WorkorderTypeSelect from "../../../components/data/workorder-type/WorkorderTypeSelect";
import WorkorderOriginSelect from "../../../components/data/workorder-origin/WorkorderOriginSelect";
import SimpleSelect from "../../../components/data/simple-select/SimpleSelect";
import { createDateQuery, createDateRangeQuery, createListQuery, createSingleQuery, createTextSearchQuery, SearchQuery } from "../../../services/api/search";

export interface WorkordersCustomerFilterFormProps {
  data?: Partial<EasyTech.WorkordersFilterType>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
  showTypeField?: boolean;
  showOriginField?: boolean;
}

export const getQueriesFromFilter = (filters?: Partial<EasyTech.WorkordersFilterType>, excludeType = false): SearchQuery[] => {
  const queries: SearchQuery[] = [];
  if (!filters) return queries;

  if (filters.ref) {
    queries.push(createTextSearchQuery(["ref"], filters.ref));
  }

  if (filters.technician) {
    if (Array.isArray(filters.technician)) {
      const techIds = filters.technician.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("technician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("technician.id", techIds));
      }
    } else if (typeof filters.technician === "object" && (filters.technician as any).id) {
      queries.push(createSingleQuery("technician.id", (filters.technician as any).id));
    } else if (typeof filters.technician === "string") {
      queries.push(createSingleQuery("technician.id", filters.technician));
    }
  }

  if (filters.status && filters.status.length > 0) {
    const statusList = Array.isArray(filters.status) ? filters.status : [filters.status];
    queries.push(createListQuery("status", statusList));
  }

  if (!excludeType && filters.type) {
    if (Array.isArray(filters.type)) {
      const typeIds = filters.type.map((t: any) => typeof t === "string" ? t : t.id);
      if (typeIds.length === 1) {
        queries.push(createSingleQuery("type.id", typeIds[0]));
      } else if (typeIds.length > 1) {
        queries.push(createListQuery("type.id", typeIds));
      }
    } else if (typeof filters.type === "object" && (filters.type as any).id) {
      queries.push(createSingleQuery("type.id", (filters.type as any).id));
    } else if (typeof filters.type === "string") {
      queries.push(createSingleQuery("type.id", filters.type));
    }
  }

  if (filters.origin) {
    if (Array.isArray(filters.origin)) {
      const originIds = filters.origin.map((o: any) => typeof o === "string" ? o : o.id);
      if (originIds.length === 1) {
        queries.push(createSingleQuery("origin.id", originIds[0]));
      } else if (originIds.length > 1) {
        queries.push(createListQuery("origin.id", originIds));
      }
    } else if (typeof filters.origin === "object" && (filters.origin as any).id) {
      queries.push(createSingleQuery("origin.id", (filters.origin as any).id));
    } else if (typeof filters.origin === "string") {
      queries.push(createSingleQuery("origin.id", filters.origin));
    }
  }

  if (filters.dateRange) {
    const field = filters.dateRange.field || "created";
    if (filters.dateRange.start && filters.dateRange.end) {
      queries.push(createDateRangeQuery(
        field,
        filters.dateRange.start,
        filters.dateRange.end
      ));
    } else if (filters.dateRange.start) {
      queries.push(createDateQuery(
        field,
        filters.dateRange.start,
        "GREATER_THAN"
      ));
    } else if (filters.dateRange.end) {
      queries.push(createDateQuery(
        field,
        filters.dateRange.end,
        "LESS_THAN"
      ));
    }
  }

  if (filters.disabled) {
    queries.push(createSingleQuery("enabled", false));
  }

  // TODO: Désactivé temporairement - Filtre missing purchase order
  // if (filters.missingPurchaseOrder) {
  //   queries.push(createSingleQuery("customer.purchaseOrderMandatory", true));
  //   queries.push(createSingleQuery("purchaseOrderNumber", null, "IS_NULL"));
  // }
  return queries;
};

const WorkordersCustomerFilterForm = (props: WorkordersCustomerFilterFormProps) => {
  const { showTypeField = true, showOriginField = true } = props;
  const [formData, setFormData] = useState<Partial<EasyTech.WorkordersFilterType>>({
    status: [],
    ref: ""
  });

  useEffect(() => {
    if (props.data) {
      setFormData(props.data);
    } else {
      setFormData({
        status: [],
        ref: ""
      });
    }
  }, [props.data]);

  // Gestion des changements pour les champs simples
  const handleChange = (value: any, name: string) => {
    const tmp: Partial<EasyTech.WorkordersFilterType> = structuredClone(formData);
    // Gestion spéciale pour le type "status", "technician" et "customer"
    if (name === "status") {
      tmp.status = Array.isArray(value) ? value : [value];
    } else if (name === "technician") {
      tmp.technician = value;
    } else if (name === "customer") {
      tmp.customer = value;
    } else {
      tmp[name as keyof Partial<EasyTech.WorkordersFilterType>] = value;
    }
    props.onChange(name, tmp[name as keyof Partial<EasyTech.WorkordersFilterType>]);
    setFormData(tmp);
  };

  const handleDateTypeChange = (field: string) => {
    const tmp: Partial<EasyTech.WorkordersFilterType> = structuredClone(formData);
    if (!tmp.dateRange) {
      tmp.dateRange = {
        start: undefined,
        end: undefined,
        field: field
      };
    } else {
      tmp.dateRange = {
        ...tmp.dateRange,
        field: field
      };
    }
    props.onChange("dateRange", tmp.dateRange);
    setFormData(tmp);
  };

  // Gestion spécifique des dates d'échéancier
  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    const tmp: Partial<EasyTech.WorkordersFilterType> = structuredClone(formData);
    if (!tmp.dateRange) {
      tmp.dateRange = {
        start: start ? start.toISOString() : undefined,
        end: end ? end.toISOString() : undefined,
        field: "created"
      };
    } else {
      tmp.dateRange = {
        ...tmp.dateRange,
        start: start ? start.toISOString() : undefined,
        end: end ? end.toISOString() : undefined
      };
    }
    props.onChange("dateRange", tmp.dateRange);
    setFormData(tmp);
  };

  const submit = (e: any) => {
    e.preventDefault();
    if (formData.dateRange?.start && !formData.dateRange?.end) return;
    if (formData.dateRange?.end && !formData.dateRange?.start) return;
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-workorders-filter"}>
      <div className="grid grid-cols-1 gap-4">
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:ref:label")}</label>
          <Input
            value={formData.ref || ""}
            onChange={(e) => handleChange(e.target.value, "ref")}
            className="w-full"
            size="md"
            placeholder={t("workorder:filters:ref:placeholder")}
          />
        </div>

        {/* TODO: check values errors */}

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:technician:label")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "technician")}
            value={formData.technician || undefined}
            clearable={true}
            isMulti={true}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:status:label")}</label>
          <WorkorderStatusSelect
            value={Array.isArray(formData.status) ? formData.status : (formData.status ? [formData.status] : [])}
            onChange={(values) => handleChange(values, "status")}
          />
        </div>

        {showTypeField && (
          <div className="field">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:type:label")}</label>
            <WorkorderTypeSelect
              value={formData.type}
              onChange={(types) => handleChange(types, "type")}
            />
          </div>
        )}

        {showOriginField && (
          <div className="field">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:origin:label")}</label>
            <WorkorderOriginSelect
              value={formData.origin}
              onChange={(origin) => handleChange(origin, "origin")}
            />
          </div>
        )}

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:date:label")}</label>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 w-full">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block lowercase">{t("common:type", "type")}</label>
              <SimpleSelect
                value={formData.dateRange?.field || "created"}
                options={[
                  { value: "created", label: t("workorder:date.created") },
                  { value: "schedule.start", label: t("workorder:date.scheduled_start") },
                  { value: "schedule.end", label: t("workorder:date.scheduled_end") },
                  { value: "time.start", label: t("workorder:date.real_start") },
                  { value: "time.end", label: t("workorder:date.real_end") },
                ]}
                onChange={(value) => handleDateTypeChange(value || "created")}
              />
            </div>
            <div className="flex-1 w-full">
              <RangeDatePicker
                startDate={formData.dateRange?.start}
                endDate={formData.dateRange?.end}
                onChange={handleDateRangeChange}
                placeholderText={t("workorder:filters:date:placeholder")}
              />
            </div>
          </div>
        </div>
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:disabled:label")}</label>
          <Switch
            checked={formData.disabled || false}
            onChange={(b) => handleChange(b, "disabled")}
          />
        </div>
      </div>
    </form>
  );
};

export default WorkordersCustomerFilterForm; 